/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable import/no-unused-modules */
import { Button } from '@rocco/components/button';
import { ProductTableSection } from '../component/proposal/ProductTableSection';
import { ReactComponent as APLogoBlack } from '@rocco/icons/svg/logo-new-black.svg';
import {
    PAYMENT_PROCESS_TIMEOUT_MESSAGE,
    PAYMENT_SUCCESS_MESSAGE,
    type Proposal,
} from '../type/types';

import { ArrowRightLine } from '@archipro-design/icons';

import { LoaderCircle } from 'lucide-react';
import {
    getCountryByCurrency,
    formatPrice,
    formatDate,
    formatPriceWithCurrency,
    getInvoiceDateString,
    formatDateWithDayName,
    getMembershipTermString,
    shouldShowMembershipTerm,
} from '../util/utils';
import { useToaster } from '~/modules/root/context/ToasterContext';
import { ReactComponent as APLogo } from '@rocco/icons/svg/ap-logo-135x18.svg';
import { ReactComponent as PlusIcon } from '@rocco/icons/svg/plus.svg';
import { QuoteAcceptedDialog } from '../component/accept-quote-popup/QuoteAcceptedDialog';
import TwoColumnsRow from '../component/proposal/TwoColumnsRow';
import { useCallback, useState, useEffect, useRef } from 'react';
import { usePaymentPopup } from '../hooks/usePaymentPopup';
import { useSetupIntentPopup } from '../hooks/useSetupIntentPopup';
import { useProposalStripeReturnConfirm } from '../hooks/useProposalStripeReturnConfirm';
import { useAcceptQuote } from '../hooks/useAcceptQuote';
import { useSearchParams, useFetcher, useRevalidator } from 'react-router';
import type { StopReason } from '~/hooks/use-auto-reload';
import { useAutoReload } from '~/hooks/use-auto-reload';
import type { QuoteAcceptOrDeclineMutation } from 'generated/graphql';
import { QuoteDecisionEnum } from 'generated/graphql';
import { StripePaymentPopup } from '../component/payment/StripePaymentPopup';
import { StripeSetupIntentPopup } from '../component/payment/StripeSetupIntentPopup';
import { AcceptQuotePopup } from '../component/accept-quote-popup/AcceptQuotePopup';
import { PaymentMethodTypePopup } from '../component/payment/PaymentMethodTypePopup';
import { cn } from '@rocco/index';
import { ProductSuppliersTermsOfService } from '../component/proposal/ProductSuppliersTermsOfService';
import { ScheduledPaymentsSection } from '../component/proposal/ScheduledPaymentsSection';
import { PROPOSAL_TYPO_CLASS } from '../component/proposal/proposal-styles';
import { PROPOSAL_GENERATE_PDF_API_ROUTE } from '../constants/api-routes';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import TeamMemberCard from '~/modules/page-builder/component/edit/invoice-module/TeamMemberCard';

type ViewMode = 'normal' | 'preview' | 'pdf';
interface ProposalSectionProps {
    proposal: Proposal;
    /** View mode: 'normal' for interactive, 'preview' for read-only preview, 'pdf' for print-ready view */
    viewMode?: ViewMode;
}

/**
 * Applies demo data to a proposal for Figma design previews.
 * When demo=figma_invoice1, overrides proposal fields to match the Figma invoice design.
 */
const demoProposal = (proposal: Proposal, demo: string): void => {
    if (demo === 'figma_invoice1') {
        proposal.invoiceNumber = 'ARC-1280-751';
        proposal.poNumber = '9812831001–R4VI';
        proposal.docType = 'invoice';
        proposal.currency = 'NZD';
        proposal.startDate = '2025-03-28';
        proposal.endDate = '2026-03-28';
        proposal.membershipTerm = '28/03/2025 - 28/03/2026';
        proposal.invoiceSentDate = new Date('2025-03-28').getTime();
        proposal.discounts = 4000; // $4,000.00
        proposal.subtotal = 23820; // $23,820.00
        proposal.tax = 2382; // $2,382.00 (10%)
        proposal.total = 34202; // $34,202.00
        proposal.createdAt = new Date('2025-08-11').getTime();

        proposal.proposalTemplate = {
            ...proposal.proposalTemplate,
            subHeadline:
                "Hi Kieran, we're excited to work with you on staying top-of-mind for anyone planning, sourcing, or specifying their architectural projects. Please don't hesitate to contact me if you have any questions about this quote.",
            paymentTerms:
                'Upon acceptance of the quote, payment can be made by credit card or bank transfer to our bank details provided below.',
            bankTransferTerms: `<p>Please use Invoice Number as payment reference</p>
<br />
<table style="width:100%;border-collapse:collapse;">
    <tr>
        <td style="vertical-align:top;font-weight:bold;">Account name:</td>
        <td style="vertical-align:top;">ArchiPro Pty Ltd</td>
    </tr>
    <tr>
        <td style="vertical-align:top;font-weight:bold;">Account number:</td>
        <td style="vertical-align:top;">1106 0465</td>
    </tr>
    <tr>
        <td style="vertical-align:top;font-weight:bold;">BSB code:</td>
        <td style="vertical-align:top;">063-167</td>
    </tr>
    <tr>
        <td style="vertical-align:top;font-weight:bold;">BIC/SWIFT code:</td>
        <td style="vertical-align:top;">CTBAAU2S</td>
    </tr>
    <tr>
        <td style="width:160px;vertical-align:top;font-weight:bold;">Payment reference:</td>
        <td style="vertical-align:top;">976986gyhio</td>
    </tr>
    <tr>
        <td style="width:160px;vertical-align:top;font-weight:bold;">Please note:</td>
        <td style="vertical-align:top;">We do not accept cheques.</td>
    </tr>
</table>`,
            creditCardTerms:
                'Please enter your credit card details from this invoice link, make payment for the first payment and the rest will auto-bill. Please note there is an additional 2.7% fee if you choose to pay via credit card.',
            accountDetails: `ArchiPro Pty Ltd<br>Level 3 Textile Centre<br>117-125 Saint Georges Bay Rd<br>Parnell, Auckland 1052<br><br>ABN: 50 620 829361<br>GST: 113-829-257`,
            contactInfo: `Client Legal Entity Name<br>2 Wella Way<br>1090 Auckland<br><br>Kieran Berger<br>kieran@zeqiri.co.nz`,
            scope: `<p>✓&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;12 months of premium discoverability to 2M design focused users</p>
<p>✓&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Dedicated support & tailored marketing campaigns</p>
<p>✓&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Placement in our industry leading publications</p>
<p>✓&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Dedicated support & tailored marketing campaigns</p>`,
            contact: {
                ...proposal.proposalTemplate.contact,
                companyName: 'Client Legal Entity Name',
                address: '2 Wella Way',
                postCode: '1090',
                city: 'Auckland',
                fullName: 'Kieran Berger',
                email: 'kieran@zeqiri.co.nz',
            },
            businessTerms: proposal.proposalTemplate.businessTerms?.length
                ? proposal.proposalTemplate.businessTerms.map((term, i) =>
                      i === 0
                          ? {
                                title: 'ArchiPro Product Supplier Terms of Service (AU)',
                                body: term.body,
                                url: term.url,
                            }
                          : term,
                  )
                : [
                      {
                          title: 'ArchiPro Product Supplier Terms of Service (AU)',
                          body: '',
                          url: '/terms-of-use',
                      },
                  ],
        };

        proposal.dealOwner = {
            ...proposal.dealOwner,
            id: 'demo-1',
            name: 'Joel Bremner',
            email: 'Joel@archipro.co.nz',
            phone: '021 0876 4321',
            avatarUrl:
                'https://dev.archipro.co.nz/images/s1/MemberUploads/q0JkbU8-cute-dog-wallpapers-v2.jpg/eyJlZGl0cyI6W3sidHlwZSI6InJlc2l6ZSIsIm9wdGlvbnMiOnsid2lkdGgiOjEwMCwiaGVpZ2h0IjoxMDAsImZpdCI6ImNvdmVyIn19XSwicXVhbGl0eSI6OTJ9',
        };

        proposal.signature = {
            ...proposal.signature,
            fullName: 'Milot Zeqiri',
            email: 'milot@archipro.co.nz',
            dataUrl: proposal.signature?.dataUrl ?? '',
            signedDate: '2026-02-26',
        };

        // Product table: 3 products matching Figma design
        const originalProducts = proposal.productItems ?? [];
        const getImageUrl = (index: number) =>
            originalProducts[index]?.imageUrl ?? '';
        const product1Desc = `12 x months platform membership including:

- Company profile
- Dedicated support team
- Networks
- Profile management editor
- Project case studies
- Company inbox
- Business Analytics & Pixel Tracker

Client marketing inclusions:
- Performance marketing campaign running across Meta & Pinterest using ArchiPro audiences. Activated across 7x newsfeed, 3x Carousel and 7x stories, 7x Pinterest Pins placements.

- 1x 1/3 width, 1x 1/2, 1x Full width in Digital magazines. 1x onsite editorial article with native drivers to generate article readership.`;
        proposal.productItems = [
            {
                id: 1,
                name: 'Supplier Membership | Premium',
                description: product1Desc,
                imageUrl: getImageUrl(0),
                quantity: 1,
                unitPrice: 23820,
                discountedUnitPrice: 22000,
                discountAmountPerUnit: 1820,
                discountApplied: true,
                taxRate: 0.1,
                totalPrice: 22000,
                totalPriceAfterTax: 24200,
                currency: 'NZD' as const,
            },
            {
                id: 2,
                name: 'Full width Ad',
                description:
                    "Full width banner ad in one of ArchiPro's Digital Publications",
                imageUrl: getImageUrl(1),
                quantity: 1,
                unitPrice: 200,
                discountedUnitPrice: 200,
                discountAmountPerUnit: 0,
                discountApplied: false,
                taxRate: 0.1,
                totalPrice: 200,
                totalPriceAfterTax: 220,
                currency: 'NZD' as const,
            },
            {
                id: 3,
                name: 'Half width Ad',
                description:
                    "Half width ad in one of ArchiPro's Digital Publications",
                imageUrl: getImageUrl(2),
                quantity: 1,
                unitPrice: 23820,
                discountedUnitPrice: 23800,
                discountAmountPerUnit: 20,
                discountApplied: true,
                taxRate: 0.1,
                totalPrice: 23800,
                totalPriceAfterTax: 26180,
                currency: 'NZD' as const,
            },
        ];

        // Scheduled payments: 3 payments matching Figma design
        proposal.paymentItems = [
            {
                id: 1,
                name: 'Payment 1',
                status: 'FullyPaid' as const,
                dueDate: new Date('2026-02-26').getTime(),
                paymentDate: new Date('2026-02-28').getTime(),
                amount: 3820,
                currency: 'NZD' as const,
            },
            {
                id: 2,
                name: 'Payment 2',
                status: 'Overdue' as const,
                dueDate: new Date('2026-03-26').getTime(),
                paymentDate: null,
                amount: 15191,
                currency: 'NZD' as const,
            },
            {
                id: 3,
                name: 'Payment 3',
                status: 'Upcoming' as const,
                dueDate: new Date('2026-04-26').getTime(),
                paymentDate: null,
                amount: 15191,
                currency: 'NZD' as const,
            },
        ];
    } else if (demo === 'figma_quote1') {
        proposal.invoiceNumber = 'QUOTE-1280-751';
    }
};

/**
 * Checks if the proposal data has changed compared to the previous version
 */
const hasProposalDataChanged = (
    currentProposal: Proposal,
    previousProposal: Proposal | null,
): boolean => {
    if (!previousProposal) return false;
    if (currentProposal.hasProcessingPayment) {
        return false;
    }

    // Compare key fields that indicate data changes
    const statusChanged = currentProposal.status !== previousProposal.status;
    const totalChanged = currentProposal.total !== previousProposal.total;
    const paymentItemsLengthChanged =
        currentProposal.paymentItems?.length !==
        previousProposal.paymentItems?.length;

    // Deep comparison of payment items
    const paymentItemsChanged =
        JSON.stringify(currentProposal.paymentItems) !==
        JSON.stringify(previousProposal.paymentItems);

    const hasChanged =
        statusChanged ||
        totalChanged ||
        paymentItemsLengthChanged ||
        paymentItemsChanged;

    return hasChanged;
};

const hasUnpaidPaymentItem = (proposal: Proposal): boolean =>
    proposal.paymentItems?.some(
        (payment) =>
            payment.status === 'Upcoming' || payment.status === 'Overdue',
    ) ?? false;

const isPaymentItemFullyPaid = (
    proposal: Proposal,
    targetPaymentId: number | null,
): boolean => {
    if (!targetPaymentId) {
        return false;
    }

    return (
        proposal.paymentItems?.some(
            (payment) =>
                payment.id === targetPaymentId &&
                payment.status === 'FullyPaid',
        ) ?? false
    );
};

/**
 * Stop auto-reload after Pay Now when a new instalment is paid, or when the
 * invoice is already settled in the UI (e.g. Stripe invoice was already paid:
 * paid count does not increase between snapshots).
 */
const hasNewPaidPayment = (
    currentProposal: Proposal,
    previousProposal: Proposal | null,
): boolean => {
    if (!previousProposal) {
        return false;
    }

    const currentPaidCount = currentProposal.paymentItems?.filter(
        (payment) => payment.status === 'FullyPaid',
    ).length;
    const previousPaidCount = previousProposal.paymentItems?.filter(
        (payment) => payment.status === 'FullyPaid',
    ).length;
    if (currentPaidCount > previousPaidCount) {
        return true;
    }

    // Same paid-instalment count but nothing left to pay (Stripe invoice was
    // already paid before both snapshots matched; avoid waiting for timeout).
    // Do not require !hasProcessingPayment — GraphQL can lag behind the DB while
    // payment rows are already FullyPaid.
    if (
        currentPaidCount === previousPaidCount &&
        currentPaidCount > 0 &&
        !hasUnpaidPaymentItem(currentProposal)
    ) {
        return true;
    }

    return false;
};

const calcLineUsed = (proposal: Proposal) => {
    let lineUsed = 56;
    proposal.productItems?.forEach((product) => {
        lineUsed += product.description.split('\n').length + 4;
    });
    lineUsed += (proposal.paymentItems?.length || 0) * 4;
    return lineUsed;
};

const ACTION_BUTTON_CLASS = 'h-12 w-full md:w-2xs';
// Desired printable width so Tailwind resolves to desktop breakpoints when printing

const ActionList = ({
    proposal,
    handleAcceptQuote,
    isMobile: _isMobile,
    canMakeQuoteDecision,
    makeQuoteDecisionFetcher,
    hasPayButton,
    onFirstPayNowClick,
    handleDownloadPdf,
    isDownloadingPdf,
}: {
    proposal: Proposal;
    handleAcceptQuote: () => void;
    isMobile: boolean;
    canMakeQuoteDecision: boolean;
    makeQuoteDecisionFetcher: ReturnType<
        typeof useFetcher<QuoteAcceptOrDeclineMutation>
    >;
    hasPayButton: boolean;
    onFirstPayNowClick: () => void;
    handleDownloadPdf: () => void;
    isDownloadingPdf: boolean;
}) => {
    return (
        <div className="md:w-xs flex w-full flex-col items-center gap-3 print:hidden">
            {proposal.docType === 'quote' && canMakeQuoteDecision && (
                <>
                    <Button
                        className={ACTION_BUTTON_CLASS}
                        color="dark"
                        size="xLarge"
                        shape="squareRounded"
                        onClick={handleAcceptQuote}
                        disabled={
                            makeQuoteDecisionFetcher.state === 'submitting'
                        }
                    >
                        <span
                            className={
                                PROPOSAL_TYPO_CLASS.ACTION_BUTTON_TEXT_ON_DARK
                            }
                        >
                            {makeQuoteDecisionFetcher.state === 'submitting' ? (
                                <>
                                    <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                                </>
                            ) : (
                                'Accept Quote'
                            )}
                        </span>
                    </Button>
                </>
            )}
            {hasPayButton && (
                <Button
                    className={ACTION_BUTTON_CLASS}
                    color="dark"
                    size="xLarge"
                    shape="squareRounded"
                    onClick={onFirstPayNowClick}
                >
                    <span
                        className={
                            PROPOSAL_TYPO_CLASS.ACTION_BUTTON_TEXT_ON_DARK
                        }
                    >
                        Pay Now
                    </span>
                </Button>
            )}
            <Button
                className={ACTION_BUTTON_CLASS}
                color="white"
                size="xLarge"
                shape="squareRounded"
                onClick={handleDownloadPdf}
                disabled={isDownloadingPdf}
            >
                <span className={PROPOSAL_TYPO_CLASS.ACTION_BUTTON_TEXT}>
                    {isDownloadingPdf ? (
                        <>
                            <LoaderCircle className="mr-2 inline h-4 w-4 animate-spin" />
                            Generating PDF...
                        </>
                    ) : (
                        'Download PDF'
                    )}
                </span>
            </Button>
        </div>
    );
};

/**
 * Displays "Quote" or "Tax Invoice" plus the reference from `proposal.docType` and
 * `invoiceNumber`. Uses docType only (not status); number is often the HubSpot deal id.
 */
const TaxInvoiceLabel = ({ proposal }: { proposal: Proposal }) => {
    if (!proposal) return null;
    const labelText = proposal.docType === 'quote' ? 'Quote' : 'Tax Invoice';
    return (
        <div className="mb-6 flex flex-row pb-[0.2rem] pl-[0.06rem]">
            <span className={PROPOSAL_TYPO_CLASS.TAX_INVOICE_KEY}>
                {labelText}{' '}
            </span>
            <span className={cn(PROPOSAL_TYPO_CLASS.TAX_INVOICE_VALUE, 'ml-2')}>
                {proposal.invoiceNumber}
            </span>
        </div>
    );
};

const IssuerDetails = ({ proposal }: { proposal: Proposal }) => {
    if (!proposal) return null;
    return (
        <div
            className={cn(
                PROPOSAL_TYPO_CLASS.ISSUSER_DETAILS,
                'flex flex-col pt-4 text-right leading-[1.33rem]',
            )}
            dangerouslySetInnerHTML={{
                __html: proposal.proposalTemplate.accountDetails || '',
            }}
        />
    );
};

const ClientDetails = ({ proposal }: { proposal: Proposal }) => {
    if (!proposal) return null;
    const SHOW_PHONE = false;

    const contactInfoHtml = proposal.proposalTemplate.contactInfo?.trim();

    if (contactInfoHtml) {
        return (
            <div className="md:pr-0.25 mb-8 flex flex-col text-right md:mt-[11.4rem] print:mt-[11.4rem]">
                <div
                    className={cn(
                        PROPOSAL_TYPO_CLASS.CLIENT_DETAILS,
                        'flex flex-col pt-4 leading-[1.33rem]',
                    )}
                    dangerouslySetInnerHTML={{ __html: contactInfoHtml }}
                />
            </div>
        );
    }

    return (
        <div className="md:pr-0.25 print:pr-0.25 mb-8 flex flex-col gap-1 text-right md:mt-[11.4rem] print:mt-[11.4rem]">
            {proposal.proposalTemplate?.contact?.companyName && (
                <p className={PROPOSAL_TYPO_CLASS.CLIENT_DETAILS}>
                    {proposal.proposalTemplate?.contact?.companyName || ''}
                </p>
            )}
            {SHOW_PHONE && (
                <p
                    className={cn(
                        PROPOSAL_TYPO_CLASS.CLIENT_DETAILS,
                        'md:mt-[0.2rem] print:mt-[0.2rem]',
                    )}
                >
                    {proposal.proposalTemplate?.contact?.phone || ''}
                </p>
            )}
            <p
                className={cn(
                    PROPOSAL_TYPO_CLASS.CLIENT_DETAILS,
                    'md:mt-[0.2rem] print:mt-[0.2rem]',
                )}
            >
                {proposal.proposalTemplate?.contact?.address || ''}
            </p>
            <p
                className={cn(
                    PROPOSAL_TYPO_CLASS.CLIENT_DETAILS,
                    'md:mt-[0.2rem] print:mt-[0.2rem]',
                )}
            >
                {proposal.proposalTemplate?.contact?.postCode || ''}{' '}
                {proposal.proposalTemplate?.contact?.city || ''}
            </p>
            <p className="mt-4"></p>
            <p
                className={cn(
                    PROPOSAL_TYPO_CLASS.CLIENT_DETAILS,
                    'md:mt-[0.1rem] print:mt-[0.1rem]',
                )}
            >
                {proposal.proposalTemplate?.contact?.fullName || ''}
            </p>
            <p
                className={cn(
                    PROPOSAL_TYPO_CLASS.CLIENT_DETAILS,
                    'md:mt-[0.1rem] print:mt-[0.1rem]',
                )}
            >
                {proposal.proposalTemplate?.contact?.email || ''}
            </p>
        </div>
    );
};

/** A summary-specific two-column row with padding and right-aligned content. */
const SummaryRow = (props: React.ComponentProps<typeof TwoColumnsRow>) => {
    const { leftSpanClassName, rightDivClassName, ...rest } = props;
    return (
        <TwoColumnsRow
            leftSpanClassName={cn(
                'md:pl-2 print:pl-2',
                PROPOSAL_TYPO_CLASS.SUMMARY_KEY,
                leftSpanClassName,
            )}
            rightDivClassName={cn(
                'md:pr-0 print:pr-0',
                PROPOSAL_TYPO_CLASS.SUMMARY_KEY,
                rightDivClassName,
            )}
            {...rest}
        />
    );
};

/** Invoice/quote details summary (dates, invoice number, PO number) shown on desktop. */
const ProposalDetails = ({
    proposal,
    country,
    isMobile,
}: {
    proposal: Proposal;
    country: 'NZ' | 'AU' | 'US';
    isMobile: boolean;
}) => {
    const list: React.ReactElement[] = [];

    if (proposal.docType === 'invoice') {
        const invoiceDateTimestamp = proposal.invoiceSentDate
            ? proposal.invoiceSentDate < 10000000000
                ? proposal.invoiceSentDate * 1000
                : proposal.invoiceSentDate
            : proposal?.signature?.signedDate
              ? new Date(proposal.signature.signedDate).getTime()
              : null;
        if (invoiceDateTimestamp) {
            list.push(
                <SummaryRow
                    key="invoice-date"
                    label="Invoice date:"
                    leftSpanClassName={PROPOSAL_TYPO_CLASS.INVOICE_INFO_KEY}
                    rightDivClassName={PROPOSAL_TYPO_CLASS.INVOICE_INFO_VALUE}
                >
                    {formatDate(invoiceDateTimestamp, country)}
                </SummaryRow>,
            );
        }
        if (proposal.invoiceNumber?.trim()) {
            list.push(
                <SummaryRow
                    key="invoice-number"
                    label="Invoice number:"
                    leftSpanClassName={PROPOSAL_TYPO_CLASS.INVOICE_INFO_KEY}
                    rightDivClassName={PROPOSAL_TYPO_CLASS.INVOICE_INFO_VALUE}
                    className="md:mt-0.25 print:mt-0.25"
                >
                    {proposal.invoiceNumber}
                </SummaryRow>,
            );
        }
    } else {
        if (proposal.createdAt) {
            list.push(
                <SummaryRow
                    key="quote-date"
                    label={isMobile ? 'Quote created' : 'Quote date:'}
                    leftSpanClassName={PROPOSAL_TYPO_CLASS.INVOICE_INFO_KEY}
                    rightDivClassName={PROPOSAL_TYPO_CLASS.INVOICE_INFO_VALUE}
                >
                    {formatDate(
                        new Date(proposal.createdAt).getTime(),
                        country,
                    )}
                </SummaryRow>,
            );
        }
        if (proposal.expiryDate) {
            list.push(
                <SummaryRow
                    key="quote-expiry-date"
                    label={isMobile ? 'Quote expires' : 'Quote expiry date:'}
                    leftSpanClassName={PROPOSAL_TYPO_CLASS.INVOICE_INFO_KEY}
                    rightDivClassName={PROPOSAL_TYPO_CLASS.INVOICE_INFO_VALUE}
                    className="md:mt-0.25 print:mt-0.25"
                >
                    {formatDate(
                        new Date(proposal.expiryDate).getTime(),
                        country,
                    )}
                </SummaryRow>,
            );
        }
    }
    if (isMobile) {
        const membershipTerm = getMembershipTermString(proposal);
        if (shouldShowMembershipTerm(proposal) && membershipTerm) {
            list.push(
                <SummaryRow
                    key="membership-term"
                    label="Membership term"
                    leftSpanClassName={PROPOSAL_TYPO_CLASS.INVOICE_INFO_KEY}
                    rightDivClassName={PROPOSAL_TYPO_CLASS.INVOICE_INFO_VALUE}
                >
                    {membershipTerm}
                </SummaryRow>,
            );
        }
    }
    if (proposal.poNumber?.trim()) {
        list.push(
            <SummaryRow
                key="po-number"
                label={isMobile ? 'PO Number' : 'Your PO number:'}
                leftSpanClassName={PROPOSAL_TYPO_CLASS.INVOICE_INFO_KEY}
                rightDivClassName={PROPOSAL_TYPO_CLASS.INVOICE_INFO_VALUE}
            >
                {proposal.poNumber}
            </SummaryRow>,
        );
    }

    if (isMobile) {
        return (
            <>
                <div className="mt-[2rem] flex w-full flex-col gap-y-[0.65rem] rounded-md bg-[#f6f6f6] py-[1.3rem] pb-[1.75rem] pl-[1.9rem] pr-[2rem]">
                    {list.map((item) => item)}
                </div>
                <div className="h-1 w-full border-b border-gray-200 pb-[2.2rem]"></div>
            </>
        );
    }

    return (
        <div className="mr-0.25 ml-12 mt-[4.26rem] flex w-[23.33rem] flex-col gap-y-[0.25rem] pl-[0.13rem]">
            {list.map((item) => item)}
        </div>
    );
};

/** A summary-specific two-column row with padding and right-aligned content. */
const NoteRow = ({
    label,
    leftSpanClassName,
    rightDivClassName,
    rightInnerHTML,
    children,
    className,
    mobileStacked = false,
}: {
    label: string;
    leftSpanClassName?: string;
    rightDivClassName?: string;
    rightInnerHTML?: string;
    children?: React.ReactNode;
    className?: string;
    mobileStacked?: boolean;
}): React.ReactElement => {
    return (
        <div
            className={cn(
                'flex items-start',
                mobileStacked
                    ? 'flex-col md:flex-row print:flex-row'
                    : 'flex-row',
                className,
            )}
        >
            <div className="w-full md:w-[11rem] print:w-[11rem]">
                <span
                    className={cn(
                        PROPOSAL_TYPO_CLASS.TERM_KEY,
                        leftSpanClassName,
                    )}
                >
                    {label}
                </span>
            </div>
            <div
                className={cn(
                    'mt-[0.2rem] w-full md:mt-0 md:w-[25rem] print:mt-0 print:w-[25rem]',
                    rightDivClassName,
                )}
            >
                {rightInnerHTML !== undefined ? (
                    <span
                        className={cn(PROPOSAL_TYPO_CLASS.TERM_VALUE)}
                        dangerouslySetInnerHTML={{ __html: rightInnerHTML }}
                    />
                ) : (
                    children
                )}
            </div>
        </div>
    );
};
/** Signature details displayed after a quote has been accepted and signed */
const ProposalSignature = ({
    signature,
    country,
    className,
    isMobile,
}: {
    signature?: Proposal['signature'];
    country: 'NZ' | 'AU' | 'US';
    /** Additional CSS classes for the root container */
    className?: string;
    /** Whether the component is rendered in mobile view */
    isMobile?: boolean;
}) => {
    if (!signature) {
        return null;
    }

    return (
        <div
            className={cn(
                'flex flex-col',

                isMobile
                    ? 'mt-8 gap-4 pt-6'
                    : 'gap-x-[1rem] gap-y-[0.93rem] pl-[0.13rem] pr-[0.7rem]',
                className,
            )}
        >
            {/* Section title */}
            <span
                className={cn(
                    PROPOSAL_TYPO_CLASS.SUMMARY_KEY,
                    'font-text pl-2',
                )}
            >
                Quote accepted by
            </span>

            {/* Signature image */}
            {signature.dataUrl && (
                <div className="flex justify-center">
                    <img
                        src={signature.dataUrl}
                        alt="Signature"
                        className={cn(isMobile ? 'w-full' : 'w-80')}
                    />
                </div>
            )}

            {/* Full name */}
            {signature.fullName && (
                <SummaryRow label="Name:" rightDivClassName="text-[#141414]">
                    {signature.fullName}
                </SummaryRow>
            )}

            {/* Email */}
            {signature.email && (
                <SummaryRow label="Email:">{signature.email}</SummaryRow>
            )}

            {/* Signed date */}
            {signature.signedDate && (
                <SummaryRow label="Date:">
                    {formatDate(
                        new Date(signature.signedDate).getTime(),
                        country,
                    )}
                </SummaryRow>
            )}
        </div>
    );
};

/**
 * Renders the Summary and CTA section with terms, payment terms, totals, and quote acceptance UI.
 */
const SummaryAndCtaSection = ({
    proposal,
    isMobile,
    shouldHideTaxColumn,
    uniformTaxRate,
    amountPaidToDate,
    amountRemaining,
    country,
    hasAcceptQuoteSignature,
    isInteractiveDisabled,
    handleAcceptQuote,
    makeQuoteDecisionFetcher,
    canMakeQuoteDecision,
}: {
    proposal: Proposal;
    isMobile: boolean;
    shouldHideTaxColumn: boolean;
    uniformTaxRate: number | null;
    amountPaidToDate: number;
    amountRemaining: number;
    country: 'NZ' | 'AU' | 'US';
    hasAcceptQuoteSignature: boolean;
    isInteractiveDisabled: boolean;
    handleAcceptQuote: () => void;
    makeQuoteDecisionFetcher: ReturnType<
        typeof useFetcher<QuoteAcceptOrDeclineMutation>
    >;
    canMakeQuoteDecision: boolean;
}) => {
    const spacingClassNameForTerms =
        'pl-[0.13rem] pr-[0.5rem] md:pl-[0.1rem] md:pr-[0.5rem] print:pl-[0.1rem] print:pr-[0.5rem]';
    const spacingClassNameForSummary =
        'pl-[0.13rem] pr-[0.5rem] md:pl-[0.2rem] md:pr-[0.8rem] print:pl-[0.2rem] print:pr-[0.8rem]';
    return (
        <div
            className={cn(
                'proposal-summary flex pb-[1rem] pt-[2rem] md:pl-[0.13rem] md:pt-[2.15rem] print:break-inside-avoid print:pl-[0.13rem] print:pt-[2.15rem]',
                isMobile ? 'flex-col' : 'flex-row justify-between',
            )}
        >
            {/* Terms */}
            <div className="flex flex-col md:w-[45rem] md:pt-0 print:w-[45rem] print:pt-0">
                <NoteRow
                    label="Payment Terms"
                    mobileStacked
                    className={cn(
                        spacingClassNameForTerms,
                        'mt-[1.5rem] md:mt-[0.1rem] print:mt-[0.1rem]',
                    )}
                    rightDivClassName="md:w-sm print:w-sm"
                    rightInnerHTML={
                        proposal.proposalTemplate.paymentTerms ?? ''
                    }
                />
                <NoteRow
                    label="Bank Transfer Payment"
                    mobileStacked
                    className={cn(
                        spacingClassNameForTerms,
                        'mt-[2rem] md:mt-[1rem] print:mt-[1rem]',
                    )}
                    rightInnerHTML={
                        proposal.proposalTemplate.bankTransferTerms ?? ''
                    }
                />
                <NoteRow
                    label="Credit Card Payment"
                    mobileStacked
                    className={cn(
                        spacingClassNameForTerms,
                        'mt-[2rem] md:mt-[2.8rem] print:mt-[2.8rem]',
                    )}
                    rightInnerHTML={
                        proposal.proposalTemplate.creditCardTerms ?? ''
                    }
                />
            </div>
            {/* Total */}
            <div
                className={cn(
                    'md:w-lg print:w-lg flex flex-col gap-y-[0.8rem]',
                    isMobile
                        ? 'mt-8 border-t border-gray-200 pt-6'
                        : cn(spacingClassNameForSummary, 'pt-[0.26rem]'),
                )}
            >
                {proposal.discounts > 0 && (
                    <SummaryRow
                        label="Discount Applied:"
                        className={spacingClassNameForSummary}
                    >
                        {formatPrice(-proposal.discounts, proposal.currency)}
                    </SummaryRow>
                )}
                <SummaryRow
                    label="Subtotal:"
                    className={spacingClassNameForSummary}
                >
                    {formatPrice(proposal.subtotal, proposal.currency)}
                </SummaryRow>
                <SummaryRow
                    label={
                        shouldHideTaxColumn &&
                        uniformTaxRate != null &&
                        uniformTaxRate > 0
                            ? `Tax (${(uniformTaxRate * 100).toFixed(0)}%):`
                            : 'Tax:'
                    }
                    className={spacingClassNameForSummary}
                >
                    {formatPrice(proposal.tax, proposal.currency)}
                </SummaryRow>
                <SummaryRow
                    label="Total Amount Due:"
                    className={cn(
                        spacingClassNameForSummary,
                        'pt-4 md:mt-[1.06rem] print:mt-[1.06rem]',
                    )}
                >
                    {formatPriceWithCurrency(proposal.total, proposal.currency)}
                </SummaryRow>
                <SummaryRow
                    label="Amount Paid to Date:"
                    className={cn(
                        spacingClassNameForSummary,
                        'md:mt-[0.13rem] print:mt-[0.13rem]',
                    )}
                    rightDivClassName=""
                >
                    {formatPriceWithCurrency(
                        amountPaidToDate,
                        proposal.currency,
                    )}
                </SummaryRow>
                <SummaryRow
                    label="Amount Remaining:"
                    className={cn(
                        spacingClassNameForSummary,
                        'md:mt-[0.13rem] print:mt-[0.13rem]',
                    )}
                    leftSpanClassName="text-[#141414]"
                    rightDivClassName="text-[#141414]"
                >
                    {formatPriceWithCurrency(
                        amountRemaining,
                        proposal.currency,
                    )}
                </SummaryRow>
                {hasAcceptQuoteSignature && !isMobile && (
                    <ProposalSignature
                        signature={proposal?.signature}
                        country={country}
                        isMobile={false}
                        className="mt-[2rem] pt-[1rem]"
                    />
                )}
                {proposal.docType === 'quote' &&
                    !isMobile &&
                    canMakeQuoteDecision &&
                    !isInteractiveDisabled && (
                        <Button
                            color="dark"
                            className="mt-4 h-[3rem] px-8 py-3 print:hidden"
                            onClick={handleAcceptQuote}
                            disabled={
                                makeQuoteDecisionFetcher.state === 'submitting'
                            }
                        >
                            <span className="items-center justify-center text-base font-medium text-white">
                                {makeQuoteDecisionFetcher.state ===
                                'submitting' ? (
                                    <>
                                        <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                                    </>
                                ) : (
                                    <>
                                        <span
                                            className={cn(
                                                PROPOSAL_TYPO_CLASS.BUTTON_ACCEPT_QUOTE_ON_DARK,
                                                'items-center justify-center font-sans',
                                            )}
                                        >
                                            Yes, Accept Quote
                                        </span>
                                        {isMobile && (
                                            <ArrowRightLine className="mb-1 ml-4 h-4 w-4 fill-white text-white" />
                                        )}
                                    </>
                                )}
                            </span>
                        </Button>
                    )}
                {proposal.docType === 'invoice' && !isMobile && (
                    <div className="mt-0"></div>
                )}
            </div>
        </div>
    );
};

/**
 * Proposal page component that displays proposal details
 */
export const ProposalSection = ({
    proposal,
    viewMode = 'normal',
}: ProposalSectionProps) => {
    const { toast } = useToaster();
    const [isOkDialogOpen, setIsOkDialogOpen] = useState(false);
    /** Track which business terms accordions are open by index */
    const [openBusinessTermsIndex, setOpenBusinessTermsIndex] = useState<
        number | null
    >(null);
    const [isPaymentMethodTypePopupOpen, setIsPaymentMethodTypePopupOpen] =
        useState(false);
    const [searchParams, setSearchParams] = useSearchParams();

    const demoParam = searchParams.get('demo');
    if (demoParam) {
        demoProposal(proposal, demoParam);
    }

    // Determine mode from prop
    const isPdfMode = viewMode === 'pdf';
    const isPreviewMode = viewMode === 'preview';
    // In PDF or preview mode, interactions are disabled
    const isInteractiveDisabled = isPdfMode || isPreviewMode;

    // Signal to Cloudflare Browser Rendering that all images have loaded
    useEffect(() => {
        if (!isPdfMode) return;
        const markReady = () => {
            document.body.setAttribute('data-pdf-ready', 'true');
        };
        const images = Array.from(document.images);
        const unloaded = images.filter((img) => !img.complete);
        if (unloaded.length === 0) {
            markReady();
            return;
        }
        let remaining = unloaded.length;
        const onSettled = () => {
            if (--remaining === 0) markReady();
        };
        unloaded.forEach((img) => {
            img.addEventListener('load', onSettled);
            img.addEventListener('error', onSettled);
        });
        return () => {
            unloaded.forEach((img) => {
                img.removeEventListener('load', onSettled);
                img.removeEventListener('error', onSettled);
            });
        };
    }, [isPdfMode]);
    const makeQuoteDecisionFetcher = useFetcher<QuoteAcceptOrDeclineMutation>();
    const { openPopup: openPaymentPopup } = usePaymentPopup();
    const { openPopup: openSetupPopup } = useSetupIntentPopup();
    const { openPopup: openAcceptQuotePopup } = useAcceptQuote();
    const processedFetcherDataRef = useRef<{
        success?: boolean;
        message?: string;
        error?: string;
    } | null>(null);
    const latestPayNowPaymentIdRef = useRef<number | null>(null);
    const { mobile } = useAppDisplayModeContext();
    const isMobile = mobile && !isPdfMode;
    const printRef = useRef<HTMLDivElement>(null);
    const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

    const invoiceDateClass = PROPOSAL_TYPO_CLASS.TERM_KEY;

    // Page break calculation for printing
    const lineUsed = calcLineUsed(proposal);
    // When there are two pages, business terms will not have page break before the first term
    const hasTwoPages = lineUsed > 120;

    /**
     * Downloads the proposal as a PDF using the server-side Generate PDF API.
     * Fetches the PDF from the API and triggers a download in the browser.
     */
    const handleDownloadPdf = useCallback(async () => {
        setIsDownloadingPdf(true);
        try {
            const filename = `proposal-${proposal.urlSegment}.pdf`;
            const apiUrl = `${PROPOSAL_GENERATE_PDF_API_ROUTE}?url=${encodeURIComponent(proposal.urlSegment)}&filename=${encodeURIComponent(filename)}`;

            const response = await fetch(apiUrl);

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to generate PDF');
            }

            // Get the PDF blob and trigger download
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : 'Unknown error';
            toast(`Failed to download PDF: ${errorMessage}`, { type: 'error' });
        } finally {
            setIsDownloadingPdf(false);
        }
    }, [proposal.urlSegment, toast]);

    // Auto reload hook
    const {
        isAutoReloading,
        enableAutoReload,
        clearAutoReload,
        checkForStopCondition,
    } = useAutoReload<Proposal>();
    const revalidator = useRevalidator();

    const hasPayButton =
        proposal.docType === 'invoice' &&
        (proposal.status === 'Issued' || proposal.status === 'PartialPaid');
    const needUpdatePaymentMethod =
        proposal.isSplitPayment && !proposal.hasStripePaymentMethod;
    /** No card or bank flow needed when there is nothing to pay (amounts are in cents). */
    const isFreeInvoice = proposal.total <= 0;

    // Ref to track if auto-reload has been started for processing payment
    const hasStartedProcessingPaymentReloadRef = useRef(false);

    // Handle auto-reload when there's a processing payment (e.g. payment is being processed)
    // Skip in PDF mode for faster rendering.
    // Guard with !isAutoReloading to avoid overriding an auto-reload started by a
    // payment action (handlePaymentSuccess / handleSetupSuccess). Once the active
    // auto-reload finishes, this effect will re-evaluate and start its own if needed.
    useEffect(() => {
        if (isInteractiveDisabled) return; // Skip in PDF/preview mode
        if (
            proposal.hasProcessingPayment &&
            !hasStartedProcessingPaymentReloadRef.current &&
            !isAutoReloading
        ) {
            hasStartedProcessingPaymentReloadRef.current = true;
            enableAutoReload(2, 15, hasProposalDataChanged, (stopReason) => {
                if (stopReason === 'conditionFulfil') {
                    toast(PAYMENT_SUCCESS_MESSAGE);
                } else {
                    toast(PAYMENT_PROCESS_TIMEOUT_MESSAGE);
                }
            });
        }
        // Reset the ref when processing payment clears
        if (!proposal.hasProcessingPayment) {
            hasStartedProcessingPaymentReloadRef.current = false;
        }
    }, [
        proposal.hasProcessingPayment,
        enableAutoReload,
        toast,
        isInteractiveDisabled,
        isAutoReloading,
    ]);

    // Re-evaluate stop condition when loader data updates *or* when a revalidation
    // finishes (state idle). Relying on `proposal` reference alone misses updates
    // when Remix reuses the same object after revalidate.
    useEffect(() => {
        if (isInteractiveDisabled) return;
        if (!isAutoReloading) return;
        checkForStopCondition(proposal);
    }, [
        proposal,
        checkForStopCondition,
        isInteractiveDisabled,
        isAutoReloading,
        revalidator.state,
    ]);

    const getFirstUnpaidPayment = useCallback(() => {
        return proposal.paymentItems?.find(
            (payment) =>
                payment.status === 'Upcoming' || payment.status === 'Overdue',
        );
    }, [proposal.paymentItems]);
    const unpaidPayment = getFirstUnpaidPayment();

    const tryOpenUpdatePaymentMethodPopup = useCallback(() => {
        const customerId = proposal?.customer?.id;
        if (customerId) {
            if (unpaidPayment) {
                openSetupPopup(
                    customerId,
                    proposal.urlSegment,
                    proposal.stripeAccount,
                    {
                        amount: unpaidPayment.amount,
                        currency: unpaidPayment.currency,
                    },
                    String(unpaidPayment.id),
                    proposal.publishableKey,
                );
            }
        }
    }, [
        openSetupPopup,
        unpaidPayment,
        proposal.urlSegment,
        proposal.stripeAccount,
        proposal.customer.id,
        proposal.publishableKey,
    ]);

    // call onQuoteAccepted() when
    const onQuoteMakeDecisionDone = useCallback(
        (
            decision: QuoteDecisionEnum,
            invoiceStatus: string,
            message: string,
        ) => {
            if (decision === QuoteDecisionEnum.Accept) {
                if (isFreeInvoice) {
                    enableAutoReload(
                        2,
                        10,
                        hasProposalDataChanged,
                        (stopReason) => {
                            if (stopReason === 'conditionFulfil') {
                                toast(message);
                            }
                        },
                    );
                } else if (
                    needUpdatePaymentMethod ||
                    !proposal.isSplitPayment
                ) {
                    setIsPaymentMethodTypePopupOpen(true);
                    toast(message);
                } else {
                    enableAutoReload(
                        2,
                        10,
                        hasProposalDataChanged,
                        (stopReason) => {
                            if (stopReason === 'conditionFulfil') {
                                toast(message);
                            }
                        },
                    );
                }
            } else {
                toast(message);
            }
        },
        [
            needUpdatePaymentMethod,
            proposal.isSplitPayment,
            isFreeInvoice,
            toast,
            enableAutoReload,
        ],
    );

    const handlePaymentReloadStop = useCallback(
        (stopReason: StopReason) => {
            latestPayNowPaymentIdRef.current = null;
            if (stopReason === 'conditionFulfil') {
                toast(PAYMENT_SUCCESS_MESSAGE);
            } else {
                toast(PAYMENT_PROCESS_TIMEOUT_MESSAGE);
            }
        },
        [toast],
    );

    const handleConfirmFallback = useCallback(() => {
        enableAutoReload(
            2,
            10,
            (currentProposal, previousProposal) =>
                hasNewPaidPayment(currentProposal, previousProposal) ||
                isPaymentItemFullyPaid(
                    currentProposal,
                    latestPayNowPaymentIdRef.current,
                ),
            handlePaymentReloadStop,
        );
    }, [enableAutoReload, handlePaymentReloadStop]);

    const handleSetupSuccess = useCallback(() => {
        revalidator.revalidate();
        toast(PAYMENT_SUCCESS_MESSAGE);
    }, [revalidator, toast]);

    const handleSetupError = useCallback(() => {
        clearAutoReload();
        revalidator.revalidate();
    }, [clearAutoReload, revalidator]);

    const handlePaymentSuccess = useCallback(() => {
        revalidator.revalidate();
        toast(PAYMENT_SUCCESS_MESSAGE);
        latestPayNowPaymentIdRef.current = null;
    }, [revalidator, toast]);

    const handlePaymentError = useCallback(() => {
        latestPayNowPaymentIdRef.current = null;
        clearAutoReload();
        revalidator.revalidate();
    }, [clearAutoReload, revalidator]);

    useProposalStripeReturnConfirm({
        proposalUrlSegment: proposal.urlSegment,
        disabled: isInteractiveDisabled,
        toast,
        onConfirmFallback: handleConfirmFallback,
    });

    // Handle fetcher response
    // Skip in PDF mode for faster rendering
    useEffect(() => {
        if (isInteractiveDisabled) return; // Skip in PDF/preview mode
        const data = makeQuoteDecisionFetcher.data as
            | {
                  success?: boolean;
                  message?: string;
                  error?: string;
                  proposal?: {
                      status: string;
                  };
                  decision?: QuoteDecisionEnum;
              }
            | undefined;

        // Only process if we have new data and haven't processed this specific response yet
        if (data && data !== processedFetcherDataRef.current) {
            processedFetcherDataRef.current = data;

            if (data.success) {
                onQuoteMakeDecisionDone(
                    data.decision as QuoteDecisionEnum,
                    data.proposal?.status || '',
                    data.message || 'Action completed successfully',
                );
            } else if (data.error) {
                toast(data.error, { type: 'error' });
            }
        }
    }, [
        makeQuoteDecisionFetcher.data,
        toast,
        onQuoteMakeDecisionDone,
        isInteractiveDisabled,
    ]);

    const handleAcceptQuote = useCallback(() => {
        openAcceptQuotePopup(
            proposal.urlSegment,
            proposal.customer.fullName,
            proposal.customer.email,
        );
    }, [
        openAcceptQuotePopup,
        proposal.urlSegment,
        proposal.customer.fullName,
        proposal.customer.email,
    ]);

    const acceptQuote = useCallback(
        (signatureData: {
            name: string;
            email: string;
            signatureDataUrl: string;
            signedAt: string;
            pixelCount: number;
        }) => {
            makeQuoteDecisionFetcher.submit(
                {
                    UrlSegment: proposal.urlSegment,
                    Decision: QuoteDecisionEnum.Accept,
                    FullName: signatureData?.name,
                    Email: signatureData?.email,
                    SignatureDataUrl: signatureData?.signatureDataUrl,
                },
                {
                    method: 'post',
                    action: '/remix-api/quote-accept-or-decline',
                },
            );
        },
        [makeQuoteDecisionFetcher, proposal],
    );

    const closeQuoteAcceptedDialog = useCallback(() => {
        setIsOkDialogOpen(false);
        setSearchParams((prev) => {
            prev.delete('docType');
            return prev;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const onPayNowClick = useCallback(
        (paymentId: number) => {
            if (hasPayButton) {
                latestPayNowPaymentIdRef.current = paymentId;
                if (needUpdatePaymentMethod) {
                    tryOpenUpdatePaymentMethodPopup();
                } else {
                    openPaymentPopup(
                        proposal.urlSegment,
                        proposal.stripeAccount,
                        paymentId,
                        proposal.publishableKey,
                    );
                }
            }
        },
        [
            hasPayButton,
            needUpdatePaymentMethod,
            proposal.stripeAccount,
            proposal.urlSegment,
            proposal.publishableKey,
            openPaymentPopup,
            tryOpenUpdatePaymentMethodPopup,
        ],
    );

    const onFirstPayNowClick = useCallback(() => {
        if (unpaidPayment) {
            onPayNowClick(unpaidPayment.id);
        }
    }, [onPayNowClick, unpaidPayment]);

    // TODO: Allow accepting/declining Draft quotes until send feature is implemented
    const canMakeQuoteDecision = !!(
        proposal.docType === 'quote' &&
        (proposal.status === 'Draft' || proposal.status === 'Sent')
    );
    // workaround
    const hasAcceptQuoteSignature = !canMakeQuoteDecision && proposal.signature;

    const calculatePaymentTotals = useCallback(() => {
        const paidPayments =
            proposal.paymentItems?.filter(
                (payment) => payment.status === 'FullyPaid',
            ) || [];
        const amountPaidToDate = paidPayments.reduce(
            (sum, payment) => sum + payment.amount,
            0,
        );
        const amountRemaining = proposal.total - amountPaidToDate;

        return { amountPaidToDate, amountRemaining };
    }, [proposal.paymentItems, proposal.total]);

    const { amountPaidToDate, amountRemaining } = calculatePaymentTotals();
    const country = getCountryByCurrency(proposal.currency);

    // productItems follow loader order (server default_sort for line items) — same order as the invoice editor.
    const productItems = proposal.productItems ?? [];
    const taxRates = productItems.map((p) => p.taxRate);
    const allSameTaxRate =
        taxRates.length > 0 && taxRates.every((r) => r === taxRates[0]);
    const shouldHideTaxColumn = allSameTaxRate;
    const uniformTaxRate = allSameTaxRate ? (taxRates[0] ?? null) : null;
    const shouldHideDiscountColumn = proposal.discounts === 0;

    /**
     * Handle URL params for email links: ?accept=1 opens Accept Quote dialog,
     * ?download=1 triggers PDF download. Params are removed after triggering.
     */
    const hasTriggeredAcceptRef = useRef(false);
    const hasTriggeredDownloadRef = useRef(false);
    useEffect(() => {
        if (isInteractiveDisabled) return;

        const acceptParam = searchParams.get('accept');
        const downloadParam = searchParams.get('download');

        if (
            acceptParam === '1' &&
            !hasTriggeredAcceptRef.current &&
            canMakeQuoteDecision
        ) {
            hasTriggeredAcceptRef.current = true;
            handleAcceptQuote();
            setSearchParams((prev) => {
                prev.delete('accept');
                return prev;
            });
        }

        if (downloadParam === '1' && !hasTriggeredDownloadRef.current) {
            hasTriggeredDownloadRef.current = true;
            handleDownloadPdf();
            setSearchParams((prev) => {
                prev.delete('download');
                return prev;
            });
        }
    }, [
        searchParams,
        isInteractiveDisabled,
        canMakeQuoteDecision,
        handleAcceptQuote,
        handleDownloadPdf,
        setSearchParams,
    ]);

    const bgColor = isPreviewMode
        ? 'bg-[#f6f6f6]'
        : isPdfMode
          ? 'bg-white'
          : 'bg-[#D7D7D7]';
    return (
        <>
            <style>
                {`
                    // @page {
                    //     margin: 0;
                    // }
                    @media print {
                        // html,
                        // body {
                        //     margin: 0;
                        // }
                        .print-content {
                            width: 100% !important;
                            max-width: none !important;
                        }
                    }
                `}
            </style>
            <div
                className={cn(
                    'flex min-h-screen justify-center px-8 print:block print:px-0 print:py-0',
                    bgColor,
                    isPreviewMode ? 'pb-24 pt-8' : 'w-full md:py-8 print:py-8',
                )}
                style={
                    isInteractiveDisabled
                        ? { pointerEvents: 'none' }
                        : undefined
                }
            >
                {/* Hide side action column in preview/pdf mode or on mobile */}
                {!isInteractiveDisabled && !isMobile && (
                    <div className="w-xs"></div>
                )}
                <div className="flex flex-row print:block print:w-full">
                    <div
                        ref={printRef}
                        className={cn(
                            'print-content overflow-x-none w-[25.88rem] bg-white px-[0.7rem] pb-[1rem] md:mt-0 md:w-[75rem] md:px-[1.63rem] md:pt-[1.9rem] print:mt-0 print:w-[75rem] print:px-0 print:pb-0 print:pt-0',
                            isPdfMode ? '' : 'shadow-lg',
                            searchParams.get('border') === '1'
                                ? 'border border-[#000000]'
                                : '',
                        )}
                    >
                        <TaxInvoiceLabel proposal={proposal} />
                        {/* Header */}
                        <div className="proposal-header flex flex-row justify-between border-t border-gray-200 px-[0.3rem] pb-20 pt-[1rem] md:px-0 md:pt-[3.66rem] print:px-0 print:pt-[3.66rem]">
                            <div className="mt-[1.2rem] flex w-[50rem] flex-col md:mt-[1.2rem] print:mt-[1.2rem]">
                                <APLogoBlack className="h-[3.947rem] w-[5.215rem] md:h-[6.034125rem] md:w-[7.972688rem] print:h-[6.034125rem] print:w-[7.972688rem]" />
                                <div className="mt-[6.6rem] md:mt-[3.2rem] print:mt-[3.2rem]">
                                    <span
                                        className={cn(
                                            PROPOSAL_TYPO_CLASS.HEADLINE,
                                        )}
                                        dangerouslySetInnerHTML={{
                                            __html:
                                                proposal.proposalTemplate
                                                    ?.headline || 'Proposal',
                                        }}
                                    />
                                </div>
                                <div className="mb-8 mt-[1.4rem] md:mb-[1rem] md:mt-[1.93rem] md:w-[38rem] print:mb-[1rem] print:mt-[1.93rem] print:w-[38rem]">
                                    <span
                                        className={cn(
                                            PROPOSAL_TYPO_CLASS.NOTES,
                                            'md:pl-0.25 print:pl-0.25 mb-4 mt-4 leading-[1.2rem]',
                                        )}
                                        dangerouslySetInnerHTML={{
                                            __html:
                                                proposal.proposalTemplate
                                                    ?.subHeadline || '',
                                        }}
                                    />
                                </div>
                                {proposal.dealOwner &&
                                    Number(proposal.dealOwner.id) > 0 && (
                                        <TeamMemberCard
                                            className="md:mt-[1.15rem] print:mt-[1.15rem]"
                                            name={
                                                proposal.dealOwner?.name || ''
                                            }
                                            email={
                                                proposal.dealOwner?.email || ''
                                            }
                                            phone={
                                                proposal.dealOwner?.phone || ''
                                            }
                                            avatarUrl={
                                                proposal.dealOwner?.avatarUrl ||
                                                ''
                                            }
                                            showBookingLink={
                                                !!proposal.dealOwner?.bookingUrl
                                            }
                                            bookingUrl={
                                                proposal.dealOwner
                                                    ?.bookingUrl || ''
                                            }
                                            variant="proposal"
                                        />
                                    )}
                            </div>
                            {!isMobile && (
                                <div className="w-[25rem]">
                                    <IssuerDetails proposal={proposal} />
                                    <ClientDetails proposal={proposal} />
                                </div>
                            )}
                        </div>
                        <div
                            className={cn(
                                'flex w-full justify-between border-t border-gray-200 md:w-auto print:w-auto',
                                isMobile ? 'flex-col' : 'flex-row',
                            )}
                        >
                            {/* What this includes */}
                            <div className="flex w-full flex-col px-[0.3rem] md:w-[75%] md:px-0 print:w-[75%] print:px-0">
                                <div className="flex flex-row justify-between">
                                    <span
                                        className={cn(
                                            PROPOSAL_TYPO_CLASS.WHAT_INCLUDES_TITLE,
                                            'mt-[0.79rem] md:mt-[0.92rem] print:mt-[0.92rem]',
                                        )}
                                    >
                                        What this includes:
                                    </span>
                                    {isMobile && (
                                        <span
                                            className={cn(
                                                invoiceDateClass,
                                                'mt-[0.79rem] pr-[0.43rem]',
                                            )}
                                        >
                                            {/* Mon 11 Aug, 2025 */}
                                            {getInvoiceDateString(
                                                proposal,
                                                isMobile,
                                            )}
                                        </span>
                                    )}
                                </div>
                                <div
                                    className={cn(
                                        PROPOSAL_TYPO_CLASS.WHAT_INCLUDES_TEXT,
                                        'w-xs ml-[0.4rem] mt-[1.93rem] space-y-2 leading-[1.32] md:ml-0 md:mt-[2rem] md:w-full md:leading-[1.2] print:ml-0 print:mt-[2rem] print:w-full print:leading-[1.2]',
                                    )}
                                    dangerouslySetInnerHTML={{
                                        __html:
                                            proposal.proposalTemplate.scope ??
                                            '',
                                    }}
                                />
                            </div>
                            <ProposalDetails
                                proposal={proposal}
                                country={country}
                                isMobile={isMobile}
                            />
                        </div>

                        {/* Products Table */}
                        <ProductTableSection
                            proposal={proposal}
                            isMobile={isMobile}
                            shouldHideTaxColumn={shouldHideTaxColumn}
                            shouldHideDiscountColumn={shouldHideDiscountColumn}
                        />

                        <SummaryAndCtaSection
                            proposal={proposal}
                            isMobile={isMobile}
                            shouldHideTaxColumn={shouldHideTaxColumn}
                            uniformTaxRate={uniformTaxRate}
                            amountPaidToDate={amountPaidToDate}
                            amountRemaining={amountRemaining}
                            country={country}
                            hasAcceptQuoteSignature={!!hasAcceptQuoteSignature}
                            isInteractiveDisabled={isInteractiveDisabled}
                            handleAcceptQuote={handleAcceptQuote}
                            makeQuoteDecisionFetcher={makeQuoteDecisionFetcher}
                            canMakeQuoteDecision={canMakeQuoteDecision}
                        />

                        {isMobile && proposal.signature && (
                            <ProposalSignature
                                signature={proposal?.signature}
                                country={country}
                                isMobile={true}
                                className="mb-8"
                            />
                        )}

                        <ScheduledPaymentsSection
                            proposal={proposal}
                            isMobile={isMobile}
                            isPdf={isPdfMode}
                            onPayNowClick={onPayNowClick}
                            hasPayButton={hasPayButton}
                            unpaidPayment={unpaidPayment}
                        />

                        {/* Footer */}
                        <div
                            className={
                                'proposal-footer mt-[8.26rem] flex w-full flex-col'
                            }
                        >
                            {proposal.proposalTemplate.businessTerms?.map(
                                (term, index) =>
                                    term.body && (
                                        <div
                                            key={index}
                                            className={
                                                index === 0 && hasTwoPages
                                                    ? ''
                                                    : 'print:break-before-page'
                                            }
                                        >
                                            {/* Hide accordion toggle button in PDF mode, show header as static text */}
                                            {isPdfMode ? (
                                                <div className="flex min-h-[2.5rem] w-full flex-row items-center justify-between border-t border-gray-200 md:h-[2.9rem] print:h-[2.9rem]">
                                                    <div
                                                        className={cn(
                                                            PROPOSAL_TYPO_CLASS.FOOTER_TERMS_TITLE,
                                                            'md:pl-0.25 print:pl-0.25 py-[0.5rem] text-left',
                                                        )}
                                                    >
                                                        {term.title || 'Terms'}
                                                    </div>
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() =>
                                                        setOpenBusinessTermsIndex(
                                                            openBusinessTermsIndex ===
                                                                index
                                                                ? null
                                                                : index,
                                                        )
                                                    }
                                                    className="flex min-h-[2.5rem] w-full flex-row items-center justify-between border-t border-gray-200 py-[0.5rem] transition-colors hover:bg-gray-50 md:h-[2.9rem] print:h-[2.9rem]"
                                                >
                                                    <div
                                                        className={cn(
                                                            PROPOSAL_TYPO_CLASS.FOOTER_TERMS_TITLE,
                                                            'md:pl-0.25 print:pl-0.25 pl-[0.13rem] text-left',
                                                        )}
                                                    >
                                                        {term.title || 'Terms'}
                                                    </div>
                                                    <div className="pl-[1rem] pr-[0.4rem] md:pr-[0.24rem] print:pr-[0.24rem]">
                                                        <PlusIcon
                                                            className={`h-6 w-6 fill-[#929292] transition-transform duration-200 ${
                                                                openBusinessTermsIndex ===
                                                                index
                                                                    ? 'rotate-45'
                                                                    : ''
                                                            }`}
                                                        />
                                                    </div>
                                                </button>
                                            )}
                                            <ProductSuppliersTermsOfService
                                                currency={proposal.currency}
                                                isVisible={
                                                    isPdfMode ||
                                                    openBusinessTermsIndex ===
                                                        index
                                                }
                                                lineUsed={hasTwoPages ? 50 : 0}
                                                html={term.body}
                                            />
                                        </div>
                                    ),
                            )}
                            {proposal.proposalTemplate?.footerLinks?.map(
                                (link) => (
                                    <a
                                        href={link.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        key={link.title}
                                    >
                                        <div className="flex min-h-[2.5rem] w-full flex-row items-center justify-between border-t border-gray-200">
                                            <div
                                                className={
                                                    PROPOSAL_TYPO_CLASS.FOOTER_TERMS_TITLE
                                                }
                                            >
                                                {link.title}
                                            </div>
                                            <div className="pl-[1rem] pr-[0.4rem] md:pr-[0.24rem] print:pr-[0.24rem]">
                                                <PlusIcon className="h-6 w-6 fill-[#929292]" />
                                            </div>
                                        </div>
                                    </a>
                                ),
                            ) || []}
                            <div
                                className={cn(
                                    'flex h-[5rem] flex-row border-t border-gray-200 pt-[1.86rem] md:pt-[1.8rem] print:pt-[1.8rem]',
                                    isMobile
                                        ? 'justify-center'
                                        : 'justify-between',
                                )}
                            >
                                {!isMobile && (
                                    <div
                                        className={
                                            PROPOSAL_TYPO_CLASS.FOOTER_TEXT
                                        }
                                    >
                                        ArchiPro,{' '}
                                        {formatDateWithDayName(
                                            proposal.createdAt,
                                            country,
                                        )}
                                    </div>
                                )}
                                <APLogo className="md:mr-[0.4rem] print:mr-[0.4rem]" />
                            </div>
                            {/* Hide action buttons in preview/pdf mode */}
                            {!isInteractiveDisabled && isMobile && (
                                <div className="mb-4 mt-[1.18rem] flex flex-col items-center gap-3 px-[0.13rem]">
                                    <ActionList
                                        proposal={proposal}
                                        handleAcceptQuote={handleAcceptQuote}
                                        isMobile={true}
                                        canMakeQuoteDecision={
                                            canMakeQuoteDecision
                                        }
                                        makeQuoteDecisionFetcher={
                                            makeQuoteDecisionFetcher
                                        }
                                        hasPayButton={hasPayButton}
                                        onFirstPayNowClick={onFirstPayNowClick}
                                        handleDownloadPdf={handleDownloadPdf}
                                        isDownloadingPdf={isDownloadingPdf}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                {/* Hide action buttons in preview/pdf mode */}
                {!isInteractiveDisabled && !isMobile && (
                    <div className="md:w-xs print:w-xs flex flex-col items-center gap-3">
                        <ActionList
                            proposal={proposal}
                            handleAcceptQuote={handleAcceptQuote}
                            isMobile={false}
                            canMakeQuoteDecision={canMakeQuoteDecision}
                            makeQuoteDecisionFetcher={makeQuoteDecisionFetcher}
                            hasPayButton={hasPayButton}
                            onFirstPayNowClick={onFirstPayNowClick}
                            handleDownloadPdf={handleDownloadPdf}
                            isDownloadingPdf={isDownloadingPdf}
                        />
                    </div>
                )}
                {!isInteractiveDisabled && (
                    <>
                        <QuoteAcceptedDialog
                            isOpen={isOkDialogOpen}
                            closeDialog={closeQuoteAcceptedDialog}
                        />
                        <StripePaymentPopup
                            onPaymentSuccess={handlePaymentSuccess}
                            onPaymentError={handlePaymentError}
                            onConfirmFallback={handleConfirmFallback}
                        />
                        <StripeSetupIntentPopup
                            onSetupSuccess={handleSetupSuccess}
                            onSetupError={handleSetupError}
                            onConfirmFallback={handleConfirmFallback}
                        />
                        <AcceptQuotePopup
                            onConfirm={acceptQuote}
                            isLoading={
                                makeQuoteDecisionFetcher.state === 'submitting'
                            }
                            termsUrl={
                                proposal.proposalTemplate.businessTerms[0]
                                    ?.url ?? '/terms-of-use'
                            }
                        />
                        <PaymentMethodTypePopup
                            isOpen={isPaymentMethodTypePopupOpen}
                            onClose={() =>
                                setIsPaymentMethodTypePopupOpen(false)
                            }
                            onPayOnline={onFirstPayNowClick}
                        />
                    </>
                )}
                {/* Auto-reload loading overlay */}
                {isAutoReloading && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                        <div className="flex flex-col items-center gap-4 rounded-lg bg-white p-8 shadow-lg">
                            <LoaderCircle className="text-blue-600 h-8 w-8 animate-spin" />
                            <p className="text-lg font-medium text-gray-700">
                                Processing...
                            </p>
                            <p className="text-sm text-gray-500">
                                Please wait while the payment is being
                                processed.
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};
